import { useEffect, useState, useMemo } from "react";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import MultiPhotoUploader from "@/components/MultiPhotoUploader";

const empty = {
  name: "", slug: "", short_description: "", long_description: "",
  base_price: 0, unit: "per piece", category: "tarpaulin",
  photos: [], cover_photo: null, active: true,
  finishing_options: [], raw_materials: [], sort_order: 0,
  thickness_feel: "", best_used_for: "",
};

const PrintServicesPage = () => {
  const [services, setServices] = useState([]);
  const [settings, setSettings] = useState(null);
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [s, st, m] = await Promise.all([
          api.get("/print-services"), api.get("/settings"), api.get("/raw-materials")
        ]);
        if (!cancelled) { setServices(s.data); setSettings(st.data); setMaterials(m.data); }
      } catch {
        if (!cancelled) toast.error("Failed to load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const fetchAll = async () => {
    const { data } = await api.get("/print-services");
    setServices(data);
  };

  const categoryFinishings = useMemo(() => {
    if (!settings) return [];
    const cat = settings.categories.find((c) => c.slug === form.category);
    return cat?.finishing_options || [];
  }, [settings, form.category]);

  const openCreate = () => {
    const firstCat = settings?.categories?.[0]?.slug || "tarpaulin";
    setEditing(null);
    setForm({ ...empty, category: firstCat });
    setOpen(true);
  };
  const openEdit = (s) => {
    setEditing(s);
    setForm({ ...empty, ...s, finishing_options: s.finishing_options || [], raw_materials: s.raw_materials || [] });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      const payload = { ...form, base_price: parseFloat(form.base_price) || 0, sort_order: parseInt(form.sort_order) || 0 };
      if (editing) await api.patch(`/print-services/${editing.service_id}`, payload);
      else await api.post("/print-services", payload);
      toast.success("Saved");
      setOpen(false);
      fetchAll();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete service "${s.name}"?`)) return;
    try { await api.delete(`/print-services/${s.service_id}`); toast.success("Deleted"); fetchAll(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const toggleFinishing = (f) => {
    const has = form.finishing_options.includes(f);
    setForm({ ...form, finishing_options: has ? form.finishing_options.filter((x) => x !== f) : [...form.finishing_options, f] });
  };

  const addRawMaterial = () => {
    setForm({ ...form, raw_materials: [...form.raw_materials, { material_id: "", qty: 1 }] });
  };
  const updateRawMaterial = (idx, patch) => {
    const next = [...form.raw_materials];
    next[idx] = { ...next[idx], ...patch };
    // Denormalize material info for display + future cost calc
    if (patch.material_id) {
      const m = materials.find((x) => x.material_id === patch.material_id);
      if (m) { next[idx].name = m.name; next[idx].unit_cost = m.price; next[idx].unit_label = m.unit; }
    }
    setForm({ ...form, raw_materials: next });
  };
  const removeRawMaterial = (idx) =>
    setForm({ ...form, raw_materials: form.raw_materials.filter((_, i) => i !== idx) });

  const materialsCost = useMemo(() =>
    form.raw_materials.reduce((a, r) => a + (parseFloat(r.qty) || 0) * (parseFloat(r.unit_cost) || 0), 0),
    [form.raw_materials]
  );

  if (loading || !settings) return <div className="p-12 text-center"><Loader2 className="h-8 w-8 animate-spin mx-auto" /></div>;

  return (
    <div className="p-6 lg:p-10 pav-fade-in" data-testid="print-services-page">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#06B6D4] font-semibold">Catalog</p>
          <h1 className="font-display text-3xl font-extrabold text-[#0B1E3F] mt-1">Print services</h1>
          <p className="text-sm text-neutral-500 mt-1">Manage services. Raw materials let you track profit per service.</p>
        </div>
        <button onClick={openCreate} className="btn-accent inline-flex items-center gap-2" data-testid="service-create-button">
          <Plus className="h-4 w-4" /> Add service
        </button>
      </div>

      {services.length === 0 ? (
        <div className="pav-card text-center py-16" data-testid="services-empty">
          <p className="text-neutral-500 mb-4">No services yet.</p>
          <button onClick={openCreate} className="btn-primary inline-flex items-center gap-2">
            <Plus className="h-4 w-4" /> Create your first service
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {services.map((s) => (
            <div key={s.service_id} className="pav-card p-0 overflow-hidden flex flex-col" data-testid={`service-card-${s.service_id}`}>
              <div className="aspect-[4/3] bg-[#F4F6FB] overflow-hidden">
                {s.cover_photo ? <img src={fileUrl(s.cover_photo)} alt={s.name} className="w-full h-full object-cover" />
                  : <div className="w-full h-full flex items-center justify-center text-neutral-300">No image</div>}
              </div>
              <div className="p-4 flex-1 flex flex-col">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-display font-bold text-lg text-[#1A1A1A] leading-tight">{s.name}</h3>
                  <span className={`pav-chip text-[10px] ${s.active ? "status-completed" : "status-cancelled"}`}>{s.active ? "active" : "off"}</span>
                </div>
                <p className="text-xs text-neutral-500 mt-1 capitalize">{s.category?.replace(/_/g, " ")}</p>
                <p className="text-sm text-neutral-600 mt-2 line-clamp-2">{s.short_description}</p>
                <p className="font-display font-extrabold text-[#0B1E3F] text-xl mt-3">
                  ₱{(s.base_price || 0).toFixed(2)}
                  <span className="text-xs text-neutral-500 font-medium ml-1">{s.unit}</span>
                </p>
                <div className="mt-3 text-xs text-neutral-500 flex gap-3">
                  <span>{s.photos?.length || 0} photo{(s.photos?.length || 0) !== 1 ? "s" : ""}</span>
                  <span>·</span>
                  <span>{s.raw_materials?.length || 0} material{(s.raw_materials?.length || 0) !== 1 ? "s" : ""}</span>
                </div>
                <div className="mt-auto pt-4 flex gap-2">
                  <button onClick={() => openEdit(s)} data-testid={`service-edit-${s.service_id}`} className="flex-1 btn-outline text-sm py-1.5 inline-flex items-center justify-center gap-1.5">
                    <Pencil className="h-3.5 w-3.5" /> Edit
                  </button>
                  <button onClick={() => remove(s)} data-testid={`service-delete-${s.service_id}`} className="px-3 py-1.5 border-2 border-red-200 text-red-600 rounded-lg hover:bg-red-50">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="service-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit service" : "New service"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Name</label>
                <input className="pav-input mt-1" data-testid="service-name-input"
                  value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Slug (optional)</label>
                <input className="pav-input mt-1" data-testid="service-slug-input"
                  value={form.slug || ""} onChange={(e) => setForm({ ...form, slug: e.target.value })} />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Short description</label>
              <input className="pav-input mt-1" data-testid="service-short-input"
                value={form.short_description} onChange={(e) => setForm({ ...form, short_description: e.target.value })} />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Long description</label>
              <textarea className="pav-input mt-1 min-h-[100px]" data-testid="service-long-input"
                value={form.long_description} onChange={(e) => setForm({ ...form, long_description: e.target.value })} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Base price ₱</label>
                <input type="number" min="0" step="0.01" className="pav-input mt-1"
                  data-testid="service-price-input"
                  value={form.base_price} onChange={(e) => setForm({ ...form, base_price: e.target.value })} />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Unit</label>
                <Select value={form.unit} onValueChange={(v) => setForm({ ...form, unit: v })}>
                  <SelectTrigger className="pav-input mt-1" data-testid="service-unit-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {settings.units.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Category</label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v, finishing_options: [] })}>
                  <SelectTrigger className="pav-input mt-1" data-testid="service-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {settings.categories.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {form.category === "tarpaulin" && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 bg-[#F4F6FB] rounded-xl p-4 border border-[#E2E8F0]" data-testid="tarpaulin-fields">
                <div>
                  <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Thickness &amp; Feel</label>
                  <input className="pav-input mt-1" data-testid="service-thickness-input"
                    placeholder="e.g. 11oz heavy-duty, glossy"
                    value={form.thickness_feel || ""}
                    onChange={(e) => setForm({ ...form, thickness_feel: e.target.value })} />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Best Used For</label>
                  <input className="pav-input mt-1" data-testid="service-best-used-input"
                    placeholder="e.g. Outdoor billboards, event backdrops"
                    value={form.best_used_for || ""}
                    onChange={(e) => setForm({ ...form, best_used_for: e.target.value })} />
                </div>
              </div>
            )}

            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600 block mb-2">
                Finishing options <span className="text-neutral-400 normal-case font-normal">(available for category: {form.category})</span>
              </label>
              {categoryFinishings.length === 0 ? (
                <p className="text-xs text-neutral-500">No finishing options configured for this category. Add them under Settings.</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {categoryFinishings.map((f) => (
                    <button key={f} type="button" onClick={() => toggleFinishing(f)}
                      data-testid={`finishing-toggle-${f}`}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold border-2 capitalize ${form.finishing_options.includes(f) ? "bg-[#0B1E3F] text-white border-[#0B1E3F]" : "bg-white text-neutral-600 border-[#E2E8F0] hover:border-[#0B1E3F]"}`}>
                      {f.replace(/_/g, " ")}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600 block mb-2">Photos · {form.photos.length}</label>
              <MultiPhotoUploader
                photos={form.photos}
                cover={form.cover_photo}
                onChange={(photos, cover) => setForm({ ...form, photos, cover_photo: cover })}
              />
            </div>

            {/* Raw Materials Section (BEFORE Active checkbox) */}
            <div className="border-2 border-dashed border-[#E2E8F0] rounded-xl p-4" data-testid="raw-materials-section">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="font-display font-bold text-sm uppercase tracking-wider text-[#0B1E3F]">Raw materials</h3>
                  <p className="text-xs text-neutral-500">Used to compute profit per service. Total cost: <span className="font-bold text-[#0B1E3F]">₱{materialsCost.toFixed(2)}</span></p>
                </div>
                <button type="button" onClick={addRawMaterial} className="text-xs btn-accent inline-flex items-center gap-1.5 py-1.5 px-3" data-testid="add-raw-material-button">
                  <Plus className="h-3.5 w-3.5" /> Add material
                </button>
              </div>
              {form.raw_materials.length === 0 ? (
                <p className="text-xs text-neutral-500 text-center py-4">No materials linked. Add raw materials under Settings → Raw Materials first.</p>
              ) : (
                <div className="space-y-2">
                  {form.raw_materials.map((rm, idx) => {
                    const m = materials.find((x) => x.material_id === rm.material_id);
                    return (
                      <div key={idx} className="grid grid-cols-12 gap-2 items-center" data-testid={`raw-material-row-${idx}`}>
                        <div className="col-span-6">
                          <Select value={rm.material_id || "none"} onValueChange={(v) => updateRawMaterial(idx, { material_id: v === "none" ? "" : v })}>
                            <SelectTrigger className="pav-input text-sm" data-testid={`raw-material-select-${idx}`}><SelectValue placeholder="Choose material" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">— Choose —</SelectItem>
                              {materials.map((mat) => <SelectItem key={mat.material_id} value={mat.material_id}>{mat.name} (₱{mat.price?.toFixed(2)}/{mat.unit})</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="col-span-3">
                          <input type="number" min="0" step="0.01" className="pav-input text-sm" placeholder="Qty"
                            data-testid={`raw-material-qty-${idx}`}
                            value={rm.qty || 1} onChange={(e) => updateRawMaterial(idx, { qty: parseFloat(e.target.value) || 0 })} />
                        </div>
                        <div className="col-span-2 text-sm font-semibold text-[#0B1E3F]">
                          ₱{((rm.qty || 0) * (m?.price || 0)).toFixed(2)}
                        </div>
                        <button type="button" onClick={() => removeRawMaterial(idx)} className="col-span-1 p-1.5 hover:bg-red-50 rounded" data-testid={`raw-material-remove-${idx}`}>
                          <X className="h-4 w-4 text-red-600" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" data-testid="service-active-checkbox"
                  checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
                Active on website
              </label>
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Sort order</label>
                <input type="number" className="pav-input mt-1" data-testid="service-sort-input"
                  value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="btn-outline text-sm">Cancel</button>
            <button onClick={save} disabled={saving} className="btn-primary text-sm inline-flex items-center gap-2 disabled:opacity-50" data-testid="service-save-button">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save service
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PrintServicesPage;
